import { CommonForm } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/CommonForm";
import {
  SignUpPill,
  SignUpPillContainer,
} from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_components/SignUpPill";
import { useIncomeRanges } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useIncomeRanges";
import { useSignUpEventTracker } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { useSignUpRequest } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { Step } from "@faire/deprecated-retailer-visitor-shared/views/SignUp/Step";
import { useStrictLocalization } from "@faire/web/common/localization";
import { Button } from "@faire/web/slate/Button";
import { Flex } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useDelayedCallback } from "@faire/web/ui/hooks/useDelayedCallback";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { useFormikContext } from "formik";
import isUndefined from "lodash/isUndefined";
import React, { FC, useEffect } from "react";
import styled from "styled-components";
import * as Yup from "yup";

export type AnnualSalesData = {
  annualSales: string | undefined;
};

export const AnnualSalesForm: FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const [
    { country, reported_creation_time, otherAnnualSalesSelected },
    setSignUpRequest,
  ] = useSignUpRequest([
    "reported_creation_time",
    "user",
    "country",
    "otherAnnualSalesSelected",
  ]);
  const { incomeRangesOptions, isLoadingIncomeRanges } =
    useIncomeRanges(country);
  const { values, handleSubmit, setFieldValue, isValid, initialValues } =
    useFormikContext<AnnualSalesData>();
  const { trackFieldValue } = useSignUpEventTracker(Step.ANNUAL_SALES);
  const { annualSales } = values;

  const delayedHandleSubmit = useDelayedCallback(handleSubmit);

  const shouldAutoProgress = isUndefined(initialValues.annualSales);

  const isLessThan12Months =
    reported_creation_time === "STARTED_THIS_YEAR" ||
    reported_creation_time === "STARTED_ONE_YEAR_AGO";

  useEffect(() => {
    if (shouldAutoProgress && annualSales && !initialValues.annualSales) {
      delayedHandleSubmit(800);
    }
  }, [
    annualSales,
    delayedHandleSubmit,
    initialValues.annualSales,
    shouldAutoProgress,
  ]);

  return (
    <CommonForm loading={isLoadingIncomeRanges}>
      <AnnualSalesContainer>
        {incomeRangesOptions.map((incomeRange) => (
          <SignUpPill
            key={incomeRange.label}
            title={incomeRange.label}
            isSelected={
              incomeRange.value === annualSales && !otherAnnualSalesSelected
            }
            onClick={() => {
              trackFieldValue("annualSales", incomeRange.value ?? "");
              setSignUpRequest({
                otherAnnualSalesSelected: false,
                // used for SCC post net terms
                annualSalesRangeOption: incomeRange.key,
              });
              setFieldValue("annualSales", incomeRange.value);
            }}
            dataTestId={`PILL_${incomeRange.value}`}
          />
        ))}
        {isLessThan12Months ? (
          <SignUpPill
            key="Something else"
            isSelected={otherAnnualSalesSelected ?? false}
            onClick={() => {
              trackFieldValue(
                "annualSales",
                incomeRangesOptions?.[0]?.value ?? ""
              );
              setSignUpRequest({
                otherAnnualSalesSelected: true,
              });
              setFieldValue("annualSales", incomeRangesOptions?.[0]?.value);
            }}
            dataTestId="PILL_NOT_SURE_YET"
            title={strictLocalize({
              id: "mfsu42",
              defaultMessage: "Not sure yet",
              description: {
                text: "Title of the not sure yet pill",
                img: "https://cdn.faire.com/fastly/4bd1746e20b5f02cc6244746bade47fdf4abc47dc7945f600245201d1a4dd0f6.png",
              },
            })}
          />
        ) : null}
      </AnnualSalesContainer>
      {!shouldAutoProgress ? (
        <>
          <Spacer height="4x" />
          <Button data-test-id="next" disabled={!isValid} type="submit">
            <StrictLocalMsg
              id="m853T8"
              defaultMessage="Next"
              description={{ text: "Button to visit the next page" }}
            />
          </Button>
        </>
      ) : null}
    </CommonForm>
  );
};

export const useSchema = () => {
  const { strictLocalize } = useStrictLocalization();

  return Yup.object().shape({
    annualSales: Yup.string().required(
      strictLocalize({
        id: "PPLP3R",
        defaultMessage: "Please select an option",
      })
    ),
  });
};

const AnnualSalesContainer = styled(Flex)`
  &&& {
    display: grid;
  }
  grid-template-columns: repeat(2, 1fr);
  grid-gap: ${getSpacing(12)};

  ${responsiveMediaQueries.tabletAndAbove} {
    flex-wrap: wrap;
    justify-content: space-between;
    gap: ${getSpacing("2x")};
  }
  ${SignUpPillContainer} {
    width: 100%;
    ${responsiveMediaQueries.tabletAndAbove} {
      flex: 0 50%;
    }
  }
`;
